import { useLocation } from "react-router";
import {
    Activity,
    ArrowRightLeft,
    BarChart3,
    Bell,
    Calendar,
    CalendarRange,
    CheckSquare,
    ClipboardList,
    DoorOpen,
    Globe,
    LayoutDashboard,
    Menu,
    MessageSquare,
    Phone,
    Receipt,
    RotateCcw,
    Search,
    Settings,
    ShieldCheck,
    UserCheck,
    Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { NotificationPanel } from "./NotificationPanel";
import { Breadcrumb } from "@/components/ui/Breadcrumb";
import { HigIconBadge, higToolbarButtonClass } from "@/components/ui/hig-icon";
import { cn } from "@/lib/utils";

const PAGE_TITLES: Record<string, string> = {
    "/app/dashboard": "Dashboard",
    "/app/calendar": "Kalender",
    "/app/waiting-room": "Wartezimmer",
    "/app/reception": "Rezeption",
    "/app/tickets": "Tickets",
    "/app/inbox": "Postfach",
    "/app/ai-phone": "KI-Telefon",
    "/app/patients": "Patienten",
    "/app/billing": "Abrechnung",
    "/app/tasks": "Aufgaben",
    "/app/analytics": "Analytics",
    "/app/triage": "KI-Triage",
    "/app/recall": "Recall & Vorsorge",
    "/app/follow-ups": "Wiedervorlagen",
    "/app/referrals": "Überweisungen",
    "/app/consents": "Aufklärungen",
    "/app/schedule": "Dienstplan",
    "/app/netzwerk": "Netzwerk",
    "/app/settings": "Einstellungen",
};

const PAGE_ICONS: Record<string, LucideIcon> = {
    "/app/dashboard": LayoutDashboard,
    "/app/calendar": Calendar,
    "/app/waiting-room": DoorOpen,
    "/app/reception": UserCheck,
    "/app/tickets": ClipboardList,
    "/app/inbox": MessageSquare,
    "/app/ai-phone": Phone,
    "/app/patients": Users,
    "/app/billing": Receipt,
    "/app/tasks": CheckSquare,
    "/app/analytics": BarChart3,
    "/app/triage": Activity,
    "/app/recall": Bell,
    "/app/follow-ups": RotateCcw,
    "/app/referrals": ArrowRightLeft,
    "/app/consents": ShieldCheck,
    "/app/schedule": CalendarRange,
    "/app/netzwerk": Globe,
    "/app/settings": Settings,
};

// ── Page subtitles — F-pattern context layer ──
const PAGE_SUBTITLES: Record<string, string> = {
    "/app/dashboard": "Übersicht & Schnellzugriff",
    "/app/calendar": "Termine verwalten",
    "/app/waiting-room": "Patienten-Warteschlange",
    "/app/reception": "Empfang & Check-in",
    "/app/billing": "Rechnungen & GOÄ",
    "/app/analytics": "Auswertungen & KPIs",
};

interface TopbarProps {
    onSearchClick?: () => void;
    onMenuClick?: () => void;
}

export function Topbar({ onSearchClick, onMenuClick }: TopbarProps) {
    const location = useLocation();

    // Match page title from pathname (also partial matches for sub-routes)
    const title =
        PAGE_TITLES[location.pathname] ||
        Object.entries(PAGE_TITLES).find(([path]) => location.pathname.startsWith(path))?.[1] ||
        "MediFlow Praxis";

    const subtitle =
        PAGE_SUBTITLES[location.pathname] ||
        Object.entries(PAGE_SUBTITLES).find(([path]) => location.pathname.startsWith(path))?.[1];

    const PageIcon =
        PAGE_ICONS[location.pathname] ||
        Object.entries(PAGE_ICONS).find(([path]) => location.pathname.startsWith(path))?.[1];

    return (
        <header className="sticky top-0 z-20 flex h-16 items-center gap-3 glass px-6" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
            {/* ── Mobile hamburger ── */}
            <button
                onClick={onMenuClick}
                className={cn("md:hidden", higToolbarButtonClass)}
                aria-label="Menü öffnen"
                style={{ minHeight: 44, minWidth: 44 }}
            >
                <Menu className="h-[18px] w-[18px]" />
            </button>

            {/* ── F-Pattern Zone 1: Page Title + Context (top-left eye entry point) ── */}
            <div className="min-w-0 flex-shrink-0">
                <div className="flex items-center gap-2">
                    {PageIcon ? (
                        <HigIconBadge size="sm" className="hidden sm:inline-flex">
                            <PageIcon className="h-[15px] w-[15px]" />
                        </HigIconBadge>
                    ) : null}
                    <h1 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 tracking-tight leading-none">
                        {title}
                    </h1>
                </div>
                {subtitle ? (
                    <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5 hidden sm:block">
                        {subtitle}
                    </p>
                ) : (
                    <Breadcrumb className="hidden sm:flex mt-0.5" />
                )}
            </div>

            {/* ── F-Pattern Zone 2: Spacer (horizontal scan) ── */}
            <div className="flex-1" />

            {/* ── F-Pattern Zone 3: Action Center (right-aligned toolbar) ── */}
            <div className="flex min-w-0 items-center justify-end gap-3 md:min-w-[332px] lg:w-[420px] xl:w-[440px]">
                {/* Search — F-pattern secondary action */}
                <button
                    onClick={onSearchClick}
                    className="group inline-flex h-11 min-w-11 flex-1 items-center justify-center rounded-[14px] bg-white/72 px-3 text-sm text-neutral-500 ring-1 ring-black/[0.05] shadow-[inset_0_1px_0_rgba(255,255,255,0.85)] transition-colors duration-150 hover:bg-white hover:text-neutral-700 dark:bg-neutral-900/70 dark:text-neutral-400 dark:ring-white/10 dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] dark:hover:bg-neutral-900 dark:hover:text-neutral-200 sm:justify-start sm:gap-2.5 sm:px-3.5"
                    title="Suche (⌘K)"
                    aria-label="Suche öffnen (⌘K)"
                >
                    <Search className="h-[17px] w-[17px] shrink-0" />
                    <span className="hidden min-w-0 flex-1 truncate text-left sm:inline">Suche…</span>
                    <kbd className="ml-auto hidden rounded-md bg-neutral-50 px-1.5 py-0.5 text-2xs font-semibold text-neutral-400 opacity-60 ring-1 ring-black/[0.05] transition-opacity duration-150 group-hover:opacity-100 dark:bg-neutral-800 dark:text-neutral-500 dark:ring-white/10 sm:inline">
                        ⌘K
                    </kbd>
                </button>

                {/* Notifications — F-pattern terminal action */}
                <div className="flex h-11 w-11 shrink-0 items-center justify-center">
                    <NotificationPanel />
                </div>
            </div>
        </header>
    );
}
